import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { authService } from '@/services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'manager';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState(() => ({
    user: authService.getCurrentUser(),
    isAuthenticated: authService.isAuthenticated()
  }));

  useEffect(() => {
    // Listen for authentication state changes from AuthService
    const handleAuthStateChange = () => {
      setAuthState({
        user: authService.getCurrentUser(),
        isAuthenticated: authService.isAuthenticated()
      });
    };

    // Subscribe to auth state changes
    authService.addEventListener('authStateChanged', handleAuthStateChange);
    authService.addEventListener('userUpdated', handleAuthStateChange);

    return () => {
      // Clean up event listeners
      authService.removeEventListener('authStateChanged', handleAuthStateChange);
      authService.removeEventListener('userUpdated', handleAuthStateChange);
    };
  }, []);

  const { user, isAuthenticated } = authState;

  // Cookie mode: the session is discovered asynchronously via /auth/me, so
  // don't decide (and don't bounce to /login) until the bootstrap resolves.
  if (!authService.isSessionBootstrapped()) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Carry the page they were on. A reload can bounce through here even with a
    // valid session — cookie auth only learns about it after /auth/me answers,
    // and a refresh that loses a rotation race fails the first attempt — and
    // /login used to send everyone to "/" afterwards, silently throwing you
    // back to the farm selector from whatever page you had open.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  // Check role requirements
  if (requiredRole && user) {
    if (requiredRole === 'admin' && user.role !== 'admin') {
      return <Navigate to="/" replace />;
    }
    if (requiredRole === 'manager' && user.role && !['admin', 'manager'].includes(user.role)) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;